import React, { FC, useState } from "react";
import Pagination from "./Pagination";
import "sortable-table-pattern/dist/main.css";

// Import the sortable table initialization function
import sortableTableInit from 'sortable-table-pattern/src/sortable-table';

document.onreadystatechange = function () {
  if (document.readyState === 'complete') {
    sortableTableInit();
  }
}

interface TableColumn {
  key: string;
  label: string;
  className?: string;
}

interface TableRow {
  [key: string]: React.ReactNode;
}

interface PaginationProps {
  totalItems: number;
  itemsPerPage: number;
}

interface TableProps {
  columns: TableColumn[];
  rows: TableRow[];
  onActionClick?: (rowData: TableRow) => void;
  showPagination?: boolean;
  paginationProps?: PaginationProps;
  caption?: string;
  isSorting?: boolean;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  uploadDocumentMobileOnly?: boolean;
  managerPortalMobileOnly?: boolean;
}


const Table: FC<TableProps> = ({
  columns,
  rows,
  onActionClick,
  showPagination = false,
  paginationProps,
  caption,
  isSorting = false,
  currentPage: controlledPage,
  onPageChange,
  uploadDocumentMobileOnly = false,
  managerPortalMobileOnly = false,
}) => {
  const [internalPage, setInternalPage] = useState(1);
  
  const currentPage = controlledPage ?? internalPage;

  const usePagination = showPagination && paginationProps;
  const itemsPerPage = usePagination ? paginationProps.itemsPerPage : rows.length;
  const totalItems = usePagination ? paginationProps.totalItems : rows.length;

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const visibleRows = rows.slice(startIndex, endIndex);

  return (
    <div className="flex flex-col w-full overflow-x-auto">
      <table
        className={`data-table !mt-0 ds-data-table--stripes-odd ${isSorting ? 'ds-data-table--sortable' : ''}`}
        data-ds-variant="mobile"
      >
        <caption className={`${uploadDocumentMobileOnly ? 'mobile:!mb-0' : ''} ${managerPortalMobileOnly ? 'mobile:!mt-[16px] ' : ''}`}>
          <p className="--medium">{caption}</p>
        </caption>

        {uploadDocumentMobileOnly && <div className="tablet:!hidden desktop:!hidden">
          <hr className="-variation-1  !mb-0 " />
        </div>}

        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} scope="col" className={`!whitespace-nowrap ${col.className ?? ""} ${uploadDocumentMobileOnly ? '!pl-0 !pr-0' : ''}`}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {visibleRows.map((row, rowIndex) => {
            const isLastRow = rowIndex === visibleRows.length - 1;
            
            // Build className based on conditions
            let trClassName = '';
            if (uploadDocumentMobileOnly) {
              trClassName += 'border-b-0!   pl-0! pr-0!';
            }
            if (managerPortalMobileOnly) {
              const borderClass = isLastRow ? '' : 'border-b-0!';
              trClassName += ` ${borderClass} border-t! border-t-[#878F9B]!`;
            }
            
            return (
            <tr key={row.id as string} className={trClassName}>
              {columns.map((col) => (
                <td key={col.key} className={`text-[14px]! ${col.className ?? ""}`}>
                  <span className="mobile-only table-head">{col.label}</span>

                  {col.key === "action" && onActionClick ? (
                    <button
                      style={{ color: "#0074AD" }}
                      className="-medium hover:underline focus:underline"
                      data-analytics-type="link"
                      data-analytics-click="General click"
                      onClick={(e) => {
                        e.preventDefault();
                        onActionClick(row);
                      }}
                    >
                      {row.action}
                    </button>
                  ) : (
                    row[col.key]
                  )}
                </td>
              ))}
            </tr>
            );
          })}
        </tbody>
      </table>

      {usePagination && globalThis.window !== undefined && window.innerWidth >= 768 && (
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange ?? setInternalPage}
        />
      )}
    </div>
  );
};

export default Table;
